import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnInit,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { environment } from '@environments/environment';
import { AppNavigationCreator } from '@layout/_core/clases/app-navigation.class';
import { AppNavigation } from '@layout/_core/types';
import { TranslateModule } from '@ngx-translate/core';
import { CurrentUserService, LayoutService } from '@shared/services';
import { AppRoutesEnum } from '@shared/types';
import { ButtonDirective } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'app-navigation',
    templateUrl: './app-navigation.component.html',
    styleUrls: ['./app-navigation.component.scss'],
    imports: [
        TranslateModule,
        PopoverModule,
        NgClass,
        RouterLink,
        RouterLinkActive,
        NgTemplateOutlet,
        TooltipModule,
        ButtonDirective,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppNavigationComponent implements OnInit {
    private layoutService = inject(LayoutService);
    private router = inject(Router);
    private currentUserService = inject(CurrentUserService);

    isSidebarCollapsed = input.required<boolean>();

    toggleIsCollapsed = output<void>();

    ovMyStrains = viewChild<Popover | null>('ovStrains');
    ovMyCages = viewChild<Popover | null>('ovCages');
    ovMyAdministration = viewChild<Popover | null>('ovAdministration');

    ovMyStrainsOpened = signal<boolean>(false);
    ovMyCagesOpened = signal<boolean>(false);
    ovMyAdministrationOpened = signal<boolean>(false);

    appNavigation = signal<AppNavigation[][]>([]);
    currentUser = computed(() => this.currentUserService.currentUser());

    overlayStyleMyStrains = { 'margin-left': '93px', 'margin-top': '-75px' };
    overlayStyleMyCages = { 'margin-left': '93px', 'margin-top': '-105px' };
    overlayStyleMyAdministration = { 'margin-left': '93px', 'margin-top': '-175px' };
    overlayStyleAmi = { 'margin-left': '93px', 'margin-top': '-850px' };

    myStrainsSubmenu: AppNavigation[] = AppNavigationCreator.createMyStrainsNavigation();
    myCagesSubmenu: AppNavigation[] = AppNavigationCreator.createMyCagesNavigation();
    myAdministrationSubmenu: AppNavigation[] = AppNavigationCreator.createMyAdministrationNavigation(
        this.currentUser()
    );

    constructor() {
        this.layoutService.isSidebarCollapsed$.subscribe((isCollapsed: boolean) => {
            if (isCollapsed) {
                this.appNavigation().forEach(navList => {
                    navList.forEach(navItem => {
                        navItem.expanded = false;
                    });
                });
            }
        });
    }

    ngOnInit(): void {
        this.initAppNavigation();
    }

    navItemClick(navItem: AppNavigation): void {
        if (!navItem.submenu) {
            return;
        }

        navItem.expanded = !navItem.expanded;

        this.hideOverlays();

        if (navItem.expanded) {
            this.appNavigation().forEach(navList => {
                navList.forEach(item => {
                    if (item.expanded && item.id !== navItem.id) {
                        item.expanded = false;
                    }
                });
            });
        }

        if (this.isSidebarCollapsed()) {
            this.toggleIsCollapsed.emit();
        }
    }

    navItemHover(navItemId: string, event: any): void {
        if (!this.isSidebarCollapsed()) {
            return;
        }

        const itemTop = event.target.getBoundingClientRect().top + event.target.getBoundingClientRect().height / 2;

        switch (navItemId) {
            case 'cages':
                this.ovMyCages()?.show(event);
                this.ovMyCagesOpened.set(true);
                this.calculateOverlayPosition('cages', itemTop);
                break;

            case 'administration':
                this.ovMyAdministration()?.show(event);
                this.ovMyAdministrationOpened.set(true);
                this.calculateOverlayPosition('administration', itemTop);
                break;

            case 'strains':
                this.ovMyStrains()?.show(event);
                this.ovMyStrainsOpened.set(true);
                this.calculateOverlayPosition('strains', itemTop);
                break;

            default:
                this.hideOverlays();
                break;
        }
    }

    private calculateOverlayPosition(navItemId: 'cages' | 'administration' | 'strains', itemTop: number): void {
        setTimeout(() => {
            let overlayPanel: Popover | null = null;

            switch (navItemId) {
                case 'cages':
                    overlayPanel = this.ovMyCages() || null;
                    break;
                case 'strains':
                    overlayPanel = this.ovMyStrains() || null;
                    break;
                case 'administration':
                    overlayPanel = this.ovMyAdministration() || null;
                    break;
            }

            if (overlayPanel?.container) {
                overlayPanel.container.style.top = itemTop + 'px';
                overlayPanel.container.style.marginTop = -overlayPanel.container.clientHeight / 2 + 'px';
                overlayPanel.container.style.position = 'fixed';
            }
        });
    }

    hideOverlays(): void {
        this.ovMyCages()?.hide();
        this.ovMyCagesOpened.set(false);

        this.ovMyAdministration()?.hide();
        this.ovMyAdministrationOpened.set(false);

        this.ovMyStrains()?.hide();
        this.ovMyStrainsOpened.set(false);
    }

    isRichMenuActive(navItem: AppNavigation): boolean {
        if (this.router.url === '/') {
            return false;
        }

        if (navItem.id === 'administration') {
            if (this.router.url.includes(AppRoutesEnum.TL_HUB) || this.router.url.includes(AppRoutesEnum.GROUPS)) {
                return true;
            }
        }

        return (navItem.submenu || []).findIndex(el => el.url.includes(this.router.url)) > -1 || false;
    }

    initAppNavigation(): void {
        this.appNavigation.set([]);
        this.appNavigation().push(this.initNav());
    }

    private initNav(): AppNavigation[] {
        return [
            AppNavigationCreator.create({
                id: 'dashboard',
                label: 'toolbar.dashboard',
                url: environment.colonyUrlDashboard,
                icon: 'fa-light fa-objects-column',
                internalRoute: false,
                isHidden: false,
            }),
            AppNavigationCreator.create({
                id: 'strains',
                label: 'toolbar.strains',
                url: environment.colonyUrlStrains,
                icon: 'fa-light fa-hourglass-half',
                internalRoute: false,
                isHidden: false,
                expanded: false,
                submenu: this.myStrainsSubmenu,
            }),
            AppNavigationCreator.create({
                id: 'mice',
                label: 'toolbar.mice',
                url: environment.colonyUrlMice,
                imageSvg: 'images/icons/mice.svg',
                internalRoute: false,
                isHidden: false,
            }),
            AppNavigationCreator.create({
                id: 'breedings',
                label: 'toolbar.breedings',
                url: environment.colonyUrlBreedings,
                icon: 'fa-light fa-heart',
                internalRoute: false,
                isHidden: false,
            }),
            AppNavigationCreator.create({
                id: 'litters',
                label: 'toolbar.litters',
                url: environment.colonyUrlLitters,
                imageSvg: 'images/icons/litters.svg',
                internalRoute: false,
                isHidden: false,
            }),
            AppNavigationCreator.create({
                id: 'cages',
                label: 'toolbar.cages',
                url: environment.colonyUrlCages,
                imageSvg: 'images/icons/cages.svg',
                internalRoute: false,
                isHidden: false,
                expanded: false,
                submenu: this.myCagesSubmenu,
            }),
            AppNavigationCreator.create({
                id: 'tasks',
                label: 'toolbar.tasks',
                url: environment.colonyUrlTasks,
                imageSvg: 'images/icons/tasks.svg',
                internalRoute: false,
                isHidden: false,
            }),
            AppNavigationCreator.create({
                id: 'ami',
                label: 'toolbar.mouseIntelligence',
                url: environment.colonyUrlAmi,
                imageSvg: 'images/icons/mouse-intelligence.svg',
                internalRoute: false,
                isHidden: false,
                expanded: false,
            }),
            AppNavigationCreator.create({
                id: 'ami-calculator',
                label: 'toolbar.amiCalculator',
                url: environment.colonyUrlAmiCalculator,
                icon: 'fa-light fa-calculator',
                internalRoute: false,
                isHidden: false,
            }),
            AppNavigationCreator.create({
                id: 'administration',
                label: 'toolbar.administration',
                url: AppRoutesEnum.TL_HUB,
                icon: 'fa-light fa-screwdriver-wrench',
                internalRoute: true,
                isHidden: false,
                expanded: false,
                submenu: this.myAdministrationSubmenu,
            }),
        ];
    }
}
